import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { History, Undo2 } from "lucide-react";
import { fmtTime } from "@/lib/time";

const fmtChange = (c) =>
  `${c.label}: ${c.from.warning_minutes}/${c.from.overdue_minutes}/${c.from.critical_minutes} \u2192 ${c.to.warning_minutes}/${c.to.overdue_minutes}/${c.to.critical_minutes} min`;

export const SlaChangeLog = ({ storeId, canUndo, refreshKey, onUndone }) => {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    const q = storeId ? `?store_id=${storeId}` : "";
    api.get(`/sla/proposals${q}`).then((r) => setRows(r.data.changes)).catch(() => {});
  }, [storeId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const undo = async (id) => {
    try { await api.post(`/sla/proposals/${id}/undo`); toast.success("Change undone, previous targets restored"); load(); onUndone?.(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="bg-card border border-border" data-testid="sla-change-log">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        <History size={16} className="text-alert-amber" />
        <span className="font-display text-lg font-bold uppercase tracking-wide">Target Change History</span>
      </div>
      <div className="p-3 space-y-2">
        {rows.length === 0 && <div className="text-sm text-muted-foreground text-center py-4">No target changes yet.</div>}
        {rows.map((r) => (
          <div key={r.id} className="flex items-start justify-between gap-3 bg-secondary/40 border border-border px-4 py-3" data-testid={`sla-change-${r.id}`}>
            <div className="min-w-0">
              <div className="text-sm font-semibold">
                {r.store_name} <span className="text-muted-foreground font-normal">· by {r.changed_by_name} · {new Date(r.created_at).toLocaleDateString()} {fmtTime(r.created_at)}</span>
              </div>
              {r.changes.length === 0 && <div className="text-xs text-muted-foreground">Switched store to its own targets (values unchanged)</div>}
              {r.changes.map((c) => <div key={c.stage} className="text-xs font-mono-t text-white/80">{fmtChange(c)}</div>)}
              {r.note && <div className="text-xs text-muted-foreground mt-1 italic">"{r.note}"</div>}
              {r.status === "undone" && <div className="text-xs text-alert-red mt-1">Undone by {r.undone_by_name}</div>}
              {r.status === "superseded" && <div className="text-xs text-muted-foreground mt-1">Replaced by admin edit ({r.undone_by_name})</div>}
            </div>
            {canUndo && r.can_undo && (
              <Button size="sm" variant="outline" className="gap-1 shrink-0" onClick={() => undo(r.id)} data-testid={`sla-undo-${r.id}`}>
                <Undo2 size={14} /> Undo
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SlaChangeLog;
